import { useCallback, useState } from 'react'

import { ActionIcon } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import DateInput from 'src/components/Inputs/DateInput/DateInput'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { AllLicenseClassValues } from 'src/graphql/types/licenses'
import IconTrash from 'src/icons/IconTrash'
import {
  getEndorsementsByLicenseClass,
  getLicenseClassOptions,
} from 'src/lib/license.utils'

import PlacesAutocomplete from './AddressInput/PlacesAutocomplete'
import ApplicationCard from './ApplicationCard/ApplicationCard'
import MultiSelectInput from './MultiSelectInput/MultiSelectInput'
import SelectInput from './SelectInput/SelectInput'
import TextInput from './TextInput/TextInput'

const QuestionSelect = ({ label, editing, value, onChange }) => {
  const answer = value === undefined ? '-' : value ? 'Yes' : 'No'

  return (
    <div className="col-span-3 mt-3 grid grid-cols-3 items-center text-sm font-medium text-doubleNickel-gray-700">
      <div className="col-span-2">{label}</div>
      {editing ? (
        <select
          className="col-span-1 rounded border border-doubleNickel-gray-300 px-2 py-1"
          value={value ? value.toString() : ''}
          onChange={(event) => {
            const val = event.target.value
            onChange(
              val === 'true' ? true : val === 'false' ? false : undefined
            )
          }}
        >
          <option value="">Select an option</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      ) : (
        <div className="text-sm font-semibold text-doubleNickel-gray-700">
          {answer}
        </div>
      )}
    </div>
  )
}

const PreviousLicensesCard = ({ values = [], handleUpdateApplication }) => {
  const [editing, setEditing] = useState(false)

  const schema = z.object({
    previousLicenses: z.array(
      z.object({
        licenseId: z.string().optional().nullable(),
        licenseNumber: z.string().optional().nullable(),
        licenseClass: z.nativeEnum(AllLicenseClassValues).optional().nullable(),
        state: z.string().optional().nullable(),
        country: z.string().optional().nullable(),
        expirationDate: z.date().optional().nullable(),
        medicalExamExpirationDate: z.date().optional().nullable(),
        endorsements: z.array(z.string()).optional().nullable(),
        isLicenseSuspended: z.boolean().optional().nullable(),
        licenseSuspendedReason: z.string().optional().nullable(),
      })
    ),
  })

  const defaultLicense = {
    licenseId: undefined,
    licenseNumber: '',
    licenseClass: undefined,
    state: '',
    country: 'US',
    expirationDate: undefined,
    medicalExamExpirationDate: undefined,
    endorsements: [],
    isLicenseSuspended: undefined,
    licenseSuspendedReason: '',
  }

  const licenseList = values.map((license) => ({
    licenseId: license.licenseId,
    licenseNumber: license.licenseNumber || '',
    licenseClass: license.licenseClass,
    state: license.state || '',
    country: license.country || 'US',
    expirationDate: license?.expirationDate
      ? new Date(license.expirationDate)
      : license.expirationDate,
    medicalExamExpirationDate: license?.medicalExamExpirationDate
      ? new Date(license.medicalExamExpirationDate)
      : license.medicalExamExpirationDate,
    endorsements: license.endorsements || [],
    isLicenseSuspended: license.isLicenseSuspended,
    licenseSuspendedReason: license.licenseSuspendedReason || '',
  }))

  const form = useForm({
    initialValues: {
      previousLicenses: licenseList,
    },
    validate: zodResolver(schema),
  })

  const handleCancel = () => {
    form.setValues({
      previousLicenses: values.map((license) => ({
        ...(license?.licenseId && { licenseId: license.licenseId }),
        licenseNumber: license.licenseNumber || '',
        licenseClass: license.licenseClass,
        state: license.state || '',
        country: license.country || 'US',
        expirationDate: license?.expirationDate
          ? new Date(license.expirationDate)
          : license.expirationDate,
        medicalExamExpirationDate: license?.medicalExamExpirationDate
          ? new Date(license.medicalExamExpirationDate)
          : license.medicalExamExpirationDate,
        endorsements: license.endorsements || [],
        isLicenseSuspended: license.isLicenseSuspended,
        licenseSuspendedReason: license.licenseSuspendedReason || '',
      })),
    })
    setEditing(false)
  }

  const handleAdd = () => {
    form.insertListItem(`previousLicenses`, defaultLicense)
  }

  const handleDelete = (index) => {
    form.removeListItem(`previousLicenses`, index)
  }

  const handleSave = useCallback(() => {
    let updates = {}
    updates = {
      previousLicenses: form.values.previousLicenses,
    }

    handleUpdateApplication(updates).then((res) => {
      if (res && !res.errors) {
        setEditing(false)
        form.setValues({
          previousLicenses: res.data.updateApplication.previousLicenses.map(
            (license) => ({
              licenseId: license.licenseId,
              licenseNumber: license.licenseNumber || '',
              licenseClass: license.licenseClass,
              state: license.state || '',
              country: license.country || 'US',
              expirationDate: license?.expirationDate
                ? new Date(license.expirationDate)
                : license.expirationDate,
              medicalExamExpirationDate: license?.medicalExamExpirationDate
                ? new Date(license.medicalExamExpirationDate)
                : license.medicalExamExpirationDate,
              endorsements: license.endorsements || [],
              isLicenseSuspended: license.isLicenseSuspended,
              licenseSuspendedReason: license.licenseSuspendedReason || '',
            })
          ),
        })
      }
    })
  }, [form.values.previousLicenses])

  const getLicenseClassData = (country) => getLicenseClassOptions(country)

  return (
    <ApplicationCard
      title={'Previous Licenses'}
      editing={editing}
      isEmpty={form.values.previousLicenses.length === 0}
      setEditing={setEditing}
      isSaveDisabled={!form.isValid()}
      handleSave={handleSave}
      handleCancel={handleCancel}
      handleAdd={handleAdd}
    >
      {form.values.previousLicenses.map((license, index) => {
        const showEndorsements = license.country === 'US'
        const licenseClassData = getLicenseClassData(license.country)
        const filteredEndorsements = getEndorsementsByLicenseClass(
          license.licenseClass
        )

        return (
          <div
            key={index}
            className="mb-4 grid grid-cols-3 gap-4 rounded-lg border px-5 py-3"
          >
            <div className="col-span-2 flex flex-col justify-center text-sm font-semibold text-doubleNickel-gray-700">
              Previous License {index + 1} of{' '}
              {form.values.previousLicenses.length}
            </div>

            <ActionIcon
              className={`ml-auto h-8 w-8 border border-doubleNickel-gray-200 bg-doubleNickel-white shadow-sm hover:bg-doubleNickel-gray-200
                  ${editing ? 'visible' : 'invisible'}`}
              onClick={() => handleDelete(index)}
            >
              <IconTrash className="h-4 w-4 fill-none stroke-doubleNickel-error-500" />
            </ActionIcon>

            <TextInput
              label="License Number"
              editing={editing}
              value={license.licenseNumber}
              handleChange={(value) =>
                form.setFieldValue(
                  `previousLicenses.${index}.licenseNumber`,
                  value
                )
              }
            />

            {editing ? (
              <PlacesAutocomplete
                label={'State'}
                placeholder="Select a state from the dropdown"
                value={license}
                types={['administrative_area_level_1']}
                handleChange={(value) => {
                  const countryChanged = value.country !== license.country
                  form.setFieldValue(
                    `previousLicenses.${index}.state`,
                    value.state
                  )
                  form.setFieldValue(
                    `previousLicenses.${index}.country`,
                    value.country
                  )
                  if (countryChanged) {
                    form.setFieldValue(
                      `previousLicenses.${index}.licenseClass`,
                      null
                    )
                    form.setFieldValue(
                      `previousLicenses.${index}.endorsements`,
                      []
                    )
                  }
                }}
              />
            ) : (
              <StyledReadOnlyInput label={'State'} value={license.state} />
            )}

            <SelectInput
              label="License Class"
              editing={editing}
              value={license.licenseClass}
              data={licenseClassData}
              handleChange={(value) => {
                form.setFieldValue(
                  `previousLicenses.${index}.licenseClass`,
                  value
                )
              }}
            />

            {showEndorsements && (
              <>
                {editing ? (
                  <MultiSelectInput
                    label="Endorsements"
                    editing={editing}
                    value={license.endorsements}
                    data={filteredEndorsements}
                    handleChange={(endorsements) => {
                      form.setFieldValue(
                        `previousLicenses.${index}.endorsements`,
                        endorsements
                      )
                    }}
                  />
                ) : (
                  <StyledReadOnlyInput
                    label={'Endorsements'}
                    value={license.endorsements?.join(', ')}
                  />
                )}
              </>
            )}

            <DateInput
              label="Expiration Date"
              editing={editing}
              value={license.expirationDate}
              onChange={(value) => {
                form.setFieldValue(
                  `previousLicenses.${index}.expirationDate`,
                  value
                )
              }}
            />

            <DateInput
              label="Medical Expiration Date"
              editing={editing}
              value={license.medicalExamExpirationDate}
              onChange={(value) => {
                form.setFieldValue(
                  `previousLicenses.${index}.medicalExamExpirationDate`,
                  value
                )
              }}
            />

            <QuestionSelect
              label="Have they had any license, permit, or privilege ever been suspended, revoked,
            or denied?"
              editing={editing}
              value={license.isLicenseSuspended}
              onChange={(value) => {
                form.setFieldValue(
                  `previousLicenses.${index}.isLicenseSuspended`,
                  value
                )
                if (!value) {
                  form.setFieldValue(
                    `previousLicenses.${index}.licenseSuspendedReason`,
                    ''
                  )
                }
              }}
            />

            {license.isLicenseSuspended && (
              <TextInput
                className="col-span-3"
                label="Reason for the suspension:"
                editing={editing}
                value={license.licenseSuspendedReason}
                handleChange={(value) =>
                  form.setFieldValue(
                    `previousLicenses.${index}.licenseSuspendedReason`,
                    value
                  )
                }
              />
            )}
          </div>
        )
      })}
    </ApplicationCard>
  )
}

export default PreviousLicensesCard
